import { useCallback, useEffect, useState } from 'react';
import { useAxiosInstance } from '../../../api/AxiosInstanceContext';
import { ProgressSnapshot } from './types';

interface UseProgressResult {
  snapshot: ProgressSnapshot | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Fetch and cache the current healer's progress snapshot for the active
 * cohort. Re-fetches whenever `refreshKey` changes — callers wire this to
 * `cases` from CasesContext so the dashboard updates the moment a case is
 * submitted / unsubmitted / approved.
 *
 * `enabled=false` short-circuits the request (used when the user is not a
 * healer, or no cohort is selected yet).
 */
export function useProgress(
  enabled: boolean,
  refreshKey: unknown
): UseProgressResult {
  const axiosInstance = useAxiosInstance();
  const [snapshot, setSnapshot] = useState<ProgressSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualBump, setManualBump] = useState(0);

  const refresh = useCallback(() => setManualBump((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setSnapshot(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    axiosInstance
      .get('/profile_progress')
      .then((response) => {
        if (cancelled) return;
        setSnapshot(response.data as ProgressSnapshot);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        // 4xx / 5xx: keep the last known snapshot; surface a subtle error state.
        setError(e?.message ?? 'Could not load progress');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, axiosInstance, refreshKey, manualBump]);

  return { snapshot, isLoading, error, refresh };
}
